import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation as useI18NextTranslation } from "react-i18next";
import i18n from "../i18n";
import translations from "../i18n/translations";

const STORAGE_KEY = "casaclass.lang";
const FALLBACK_LANG = "fr";

const I18nContext = createContext({ language: FALLBACK_LANG, setLanguage: () => {}, t: (k) => k, languages: [] });

export function getStoredLanguage() {
  if (typeof window === "undefined") return FALLBACK_LANG;
  const sessionLang = window.sessionStorage.getItem(STORAGE_KEY);
  if (sessionLang && translations[sessionLang]) return sessionLang;
  const persisted = window.localStorage.getItem(STORAGE_KEY);
  if (persisted && translations[persisted]) return persisted;
  return FALLBACK_LANG;
}

export function I18nProvider({ children }) {
  const [language, setLanguageState] = useState(() => getStoredLanguage());
  const { t } = useI18NextTranslation();

  useEffect(() => {
    const nextLang = translations[language] ? language : FALLBACK_LANG;
    i18n.changeLanguage(nextLang);
    const dir = translations[nextLang]?.meta?.dir || "ltr";
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("dir", dir);
      document.documentElement.setAttribute("lang", nextLang);
    }
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, nextLang);
      window.sessionStorage.setItem(STORAGE_KEY, nextLang);
    }
  }, [language]);

  const value = useMemo(() => {
    const setLanguage = (lang) => {
      if (!translations[lang]) return;
      setLanguageState(lang);
    };
    return { language, setLanguage, t, languages: Object.keys(translations) };
  }, [language, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useTranslation() {
  return useContext(I18nContext);
}
